import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { useInvalidateWorkspaceEntitlements } from '@/core/hooks/use-entitlements';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { usePendingUpgradeStore } from '@/core/store/pending-upgrade.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

/**
 * Handles Stripe Checkout / Portal return query params on the billing page.
 */
export function useBillingReturn() {
  const location = useLocation();
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const queryClient = useQueryClient();
  const invalidateEntitlements = useInvalidateWorkspaceEntitlements();
  const workspace = useCurrentWorkspace();
  const clearPending = usePendingUpgradeStore((s) => s.clearPending);
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;

    const key = `${location.pathname}?checkout=${checkout}`;
    if (handledRef.current === key) return;
    handledRef.current = key;

    const refresh = async () => {
      if (workspace?.id) {
        invalidateEntitlements(workspace.id);
        clearPending(workspace.id);
      }
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    };

    if (checkout === 'success') {
      void refresh().then(() => {
        toast.success(billingDict.checkoutSuccess);
      });
    } else if (checkout === 'cancelled') {
      toast.info(billingDict.checkoutCancelled);
    }

    params.delete('checkout');
    params.delete('session_id');
    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [
    billingDict.checkoutCancelled,
    billingDict.checkoutSuccess,
    clearPending,
    invalidateEntitlements,
    location.pathname,
    location.search,
    queryClient,
    workspace?.id,
  ]);
}
